import { Button, toast } from '@postscript/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetUserProfile } from 'components/account/AccountView/users/hooks/useUsers';
import { ACCOUNT_STATUS_KEY } from 'components/account/useAccountStatus';
import { toastError } from 'components/billing/common/utils';
import { formatErrorMessage } from 'utils/errors';
import { useUser } from 'controllers/contexts/user';
import { api } from 'controllers/network/apiClient';
import { QueryCacheTimes } from '../../flowBuilder/constants';
import { TFNRequestResponse, TFNVerificationRequestsResponse } from '../types';
import { buildVerificationMailto } from '../utils';

const tfnVerification = 'tfn_verification';
const shopTfnHistory = 'shop_tfn_history';

export function useGetTFNVerification() {
  return useQuery({
    queryKey: [tfnVerification],
    queryFn: (): Promise<TFNVerificationRequestsResponse> =>
      api.get('/v2/messaging/verification_requests/'),
    select: (data) => data.requests[0] ?? null,
    staleTime: QueryCacheTimes.SHORT,
    onError: (error) => toast.error(formatErrorMessage(error)),
  });
}

export function useSkipTFNValidation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.post('/v2/messaging/verification_requests/skip_validation'),
    onSuccess: () => {
      queryClient.refetchQueries([tfnVerification]);
    },
    onError: toastError,
  });
}

export function useResubmitTFNVerification() {
  const queryClient = useQueryClient();
  const {
    user: { shop_name: shopName },
  } = useUser();
  const { data: profile } = useGetUserProfile();

  return useMutation({
    mutationFn: () => api.post('/v2/messaging/verification_requests/resubmit'),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: [tfnVerification] });
      queryClient.refetchQueries({ queryKey: [ACCOUNT_STATUS_KEY] });
    },
    onError: () => {
      const contactSupportLink = (
        <Button
          variant="plain"
          onClick={() =>
            window.open(
              buildVerificationMailto(
                shopName,
                profile?.firstName ?? undefined,
              ),
              '_blank',
            )
          }
          style={{
            color: 'white',
            textDecoration: 'underline',
            padding: 0,
            font: 'inherit',
          }}
          aria-label="Contact Support"
        >
          Contact Support
        </Button>
      );
      toast.error(
        'Unable to resubmit TFN for verification. Please contact support',
        contactSupportLink,
      );
    },
  });
}

export function useRequestNewTFN() {
  const queryClient = useQueryClient();
  const { getUserInfo } = useUser();

  return useMutation({
    mutationFn: () => api.post<TFNRequestResponse>('/v2/messaging/tfn/request'),
    onSuccess: () => {
      queryClient.refetchQueries([tfnVerification]);
      queryClient.refetchQueries([shopTfnHistory]);
      getUserInfo();
      toast.success('TFN request submitted.');
    },
    onError: toastError,
  });
}
